import { IonSkeletonText, IonText } from "@ionic/react";
import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { motion } from "motion/react";
import { z } from "zod";
import { backendService } from "@/lib/backend";
import { queryOptions_myPersonalMission } from "@/lib/query/functions/personal-mission/my";
import { useMutationStream } from "@/lib/query/hooks/useMutationStream";
import { Routes } from "@/lib/router";
import { cx } from "@/lib/style/cva.config";
import { jsonParse } from "@/utils/json/parse";
import { Button } from "../Global/Button";
import { Content } from "../Global/Content";
import { Markdown } from "../Global/Markdown";
import { YourMissionAvatarWithBackground } from "./AvatarWithBackground";

export function YourMissionContent(props: React.ComponentPropsWithoutRef<typeof Content>) {
	const { t } = useTranslation();
	const queryPersonalMission = useQuery(queryOptions_myPersonalMission());

	const [streamedMission, mutationStream] = useMutationStream({
		mutationKey: ["personal-mission", "create-stream"],
		mutationFn: (params) => backendService.personalMission.personalMissionControllerCreate(params),
	});

	const parsedStreamedMission = jsonParse({
		input: streamedMission ?? "",
		schema: z.object({ mission: z.string() }),
	});

	const existingMission = queryPersonalMission.data?.personalMission;
	const currentMission = existingMission ?? parsedStreamedMission?.mission;
	const isLoading = queryPersonalMission.isLoading || mutationStream.isPending;
	const showSkeleton = isLoading || (!currentMission && !streamedMission);

	useEffect(() => {
		if (queryPersonalMission.isSuccess && !existingMission) {
			mutationStream.mutate();
		}
	}, [queryPersonalMission.isSuccess, existingMission]);

	return (
		<Content {...props} className={cx("part-scroll:gap-0", props.className)}>
			<YourMissionAvatarWithBackground />

			<motion.div
				className="border-brown-200 bg-brown-100 flex flex-col gap-2 rounded-xl border p-4"
				initial={{ opacity: 0 }}
				animate={{ opacity: 1 }}
				transition={{ delay: 1 }}
			>
				<IonText className="text-brown-700 text-lg font-bold text-pretty">
					{t("your-mission.dashboard.title")}
				</IonText>

				{showSkeleton ? (
					<IonSkeletonText animated className="h-16 rounded-lg" />
				) : (
					<div className="text-brown-700 text-pretty">
						<Markdown>{currentMission ?? ""}</Markdown>
					</div>
				)}

				<Button
					size="small"
					variant="header"
					className="mt-4 flex items-center"
					disabled={mutationStream.isPending || queryPersonalMission.isLoading}
					routerLink={Routes.YourMissionChat()}
				>
					{t("your-mission.dashboard.update")}
				</Button>
			</motion.div>
		</Content>
	);
}
